import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import FrontechLogo from './FrontechLogo';
import {
  Bot, BarChart3, CreditCard, LogOut, Menu, X,
  Sun, Moon, ChevronRight, Settings, Sparkles,
} from 'lucide-react';

function WatermarkBg() {
  return (
    <div className="frontech-watermark-bg" style={{ top: '-60px', right: '-60px' }}>
      <FrontechLogo size={420} variant="watermark" />
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const links = [
    { to: '/dashboard',            label: 'Agents',    icon: Bot,       end: true },
    { to: '/dashboard/analytics',  label: 'Analytics', icon: BarChart3, end: false },
    { to: '/dashboard/billing',    label: 'Billing',   icon: CreditCard, end: false },
  ];

  // Breadcrumb label
  const getBreadcrumb = () => {
    const p = location.pathname;
    if (p === '/dashboard') return 'Agents';
    if (p.includes('/agents/new')) return 'New Agent';
    if (p.includes('/agents/') && p.includes('/edit')) return 'Edit Agent';
    if (p.includes('/agents/')) return 'Agent Detail';
    if (p.includes('/analytics')) return 'Analytics';
    if (p.includes('/billing')) return 'Billing';
    return 'Dashboard';
  };

  const userInitial = (user?.name || user?.email || 'U')[0].toUpperCase();

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--color-base)' }}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="dashboard-sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-50 w-60 flex flex-col transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{
          background: 'var(--color-surface)',
          borderRight: '1px solid var(--color-border)',
        }}
      >
        {/* Logo area */}
        <div className="h-14 flex items-center justify-between px-5" style={{ borderBottom: '1px solid var(--color-border)' }}>
          <NavLink to="/dashboard" className="flex items-center gap-2.5 group" onClick={() => setSidebarOpen(false)}>
            <FrontechLogo size={26} variant="icon" color="var(--color-accent)" />
            <div>
              <span className="font-heading font-semibold text-sm tracking-tight" style={{ color: 'var(--color-text-primary)' }}>Front</span>
              <span className="text-xs font-mono ml-1" style={{ color: 'var(--color-text-muted)' }}>by FRONTECH</span>
            </div>
          </NavLink>
          <button className="lg:hidden p-1 rounded-md cursor-pointer" style={{ color: 'var(--color-text-secondary)' }} onClick={() => setSidebarOpen(false)}>
            <X size={18} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-0.5">
          <p className="text-xs font-mono uppercase tracking-widest px-3 mb-3" style={{ color: 'var(--color-text-muted)' }}>
            Workspace
          </p>
          {links.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`sidebar-${l.label.toLowerCase()}`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-all duration-150 ${
                  isActive
                    ? 'bg-[var(--color-accent-dim)] font-semibold'
                    : 'hover:bg-[var(--color-elevated)]'
                }`
              }
              style={({ isActive }) => ({
                color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              })}
            >
              <l.icon size={17} />
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Bottom: theme toggle + user */}
        <div className="p-4 space-y-3" style={{ borderTop: '1px solid var(--color-border)' }}>
          {/* Theme toggle */}
          <button
            onClick={toggle}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-all duration-150"
            style={{
              background: 'var(--color-elevated)',
              color: 'var(--color-text-secondary)',
            }}
            aria-label="Toggle theme"
          >
            {theme === 'dark'
              ? <><Sun size={16} /><span>Light Mode</span></>
              : <><Moon size={16} /><span>Dark Mode</span></>
            }
          </button>

          {/* User card */}
          <div className="flex items-center gap-3 px-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
              style={{ background: 'var(--color-accent-dim)', color: 'var(--color-accent)' }}
            >
              {userInitial}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                {user?.name || 'User'}
              </p>
              <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                {user?.email}
              </p>
            </div>
          </div>
          <button
            data-testid="sidebar-logout"
            onClick={handleLogout}
            className="logout-btn flex items-center gap-2 text-sm px-3 py-2 rounded-lg w-full cursor-pointer transition-colors duration-150"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 flex flex-col relative overflow-hidden">
        <WatermarkBg />

        {/* Top bar */}
        <header
          className="h-14 flex items-center px-5 gap-4 sticky top-0 z-30"
          style={{
            background: 'rgba(var(--watermark-color), 0.01)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-1.5 rounded-md cursor-pointer transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={20} />
          </button>

          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-sm">
            <span style={{ color: 'var(--color-text-muted)' }}>Dashboard</span>
            <ChevronRight size={14} style={{ color: 'var(--color-text-muted)' }} />
            <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
              {getBreadcrumb()}
            </span>
          </div>

          <div className="flex-1" />

          {/* Upgrade pill */}
          <a
            href="/dashboard/billing"
            className="hidden sm:flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer transition-all"
            style={{
              background: 'var(--color-accent-dim)',
              color: 'var(--color-accent)',
              border: '1px solid rgba(0,214,126,0.15)',
            }}
          >
            <Sparkles size={12} />
            Upgrade
          </a>

          {/* Theme toggle (header) */}
          <button
            onClick={toggle}
            className="hidden lg:flex p-2 rounded-lg cursor-pointer transition-colors"
            style={{ color: 'var(--color-text-secondary)', background: 'var(--color-elevated)' }}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </header>

        {/* Page content */}
        <div className="flex-1 p-5 lg:p-7 relative z-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
